import { useQuery } from '@apollo/client';
import React, { useEffect, useMemo, useState } from 'react';
import { useGeoSearch } from 'react-instantsearch';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';
import isEmpty from 'lodash.isempty';

// Components
import PropertyMapCard from '@molecules/PropertyMapCard';
import SearchMapboxMap from '@organisms/SearchMapboxMap';

// Functionality
import { SAVED_PROPERTIES_QUERY } from '@general/graphql/properties.graph';
import { mapSearchPropertyToProps } from '@general/transforms/property.transform';
import { getMapboxInitialBounds, getAlgoliaGeoInitialBounds } from '@general/helpers/map.helpers';

const LegendContainer = styled.div`
    background: #fff;
    bottom: 0px;
    margin-bottom: 1.5rem;
    margin-left: 1.5rem;
    margin-top: 1.5rem;
    overflow-y: auto;
    position: absolute;
    padding: 1rem;
    top: 0px;
    width: 40%;
    z-index: 99;
`;

/**
 * Algolia capabilities connected to a Mapbox map via Connect GeoSearch.
 * @see https://www.algolia.com/doc/api-reference/widgets/geo-search/react/#connector
 */
export const SearchPropertiesMap = ({
    displayLegend = false,
    onPropertyFavorite = () => {},
    onPropertyClick = () => {},
}) => {
    const { items: hits, refine } = useGeoSearch();
    const { data: savedPropertiesData } = useQuery(SAVED_PROPERTIES_QUERY);
    const favoriteIds = useMemo(() => {
        if (!savedPropertiesData || !savedPropertiesData.savedProperties) {
            return [];
        }

        const filtered = savedPropertiesData.savedProperties.filter(property => !!property);
        return filtered.map(property => property.recordId);
    }, [savedPropertiesData]);

    const [markers, setMarkers] = useState([]);
    const [initialBounds, setInitialBounds] = useState([]);

    useEffect(() => {
        setMarkers(
            hits
                .filter(hit => !!hit._geoloc.lat && !!hit._geoloc.lng)
                .map(hit => ({
                    lat: hit._geoloc.lat,
                    lng: hit._geoloc.lng,
                }))
        );
    }, [hits]);

    useEffect(() => {
        if (!isEmpty(initialBounds)) {
            return;
        }

        if (markers.length === 0) {
            return;
        }

        setInitialBounds(getMapboxInitialBounds(markers));
        refine(getAlgoliaGeoInitialBounds(markers));
    }, [initialBounds, markers, refine]);

    const updateBounds = useDebouncedCallback(map => {
        const bounds = map.getBounds();
        refine({
            northEast: { lat: bounds._ne.lat, lng: bounds._ne.lng },
            southWest: { lat: bounds._sw.lat, lng: bounds._sw.lng },
        });
    }, 500);

    return (
        <div style={{ position: 'relative' }}>
            {displayLegend && (
                <LegendContainer>
                    <>
                        {hits.map((hit, hitIndex) => (
                            <PropertyMapCard
                                key={hit.recordId}
                                property={{
                                    ...mapSearchPropertyToProps({ hit }),
                                    __position: hitIndex + 1,
                                    __queryID: hits[0].__queryID,
                                }}
                                isFavorite={favoriteIds.includes(hit.recordId)}
                                favorited={onPropertyFavorite}
                                onClick={onPropertyClick}
                            />
                        ))}
                    </>
                </LegendContainer>
            )}

            <SearchMapboxMap
                markers={markers}
                initialBounds={initialBounds}
                height="600px"
                width="100%"
                onCoordinatesChange={updateBounds}
            />
        </div>
    );
};

SearchPropertiesMap.propTypes = {
    displayLegend: PropTypes.bool,
    onPropertyFavorite: PropTypes.func,
    onPropertyClick: PropTypes.func,
};
